import {styled} from '@linaria/react';
import React, {useEffect, useState} from 'react';
import {hideScrollBar, touchScroll} from '../../../constants/scrolling';
import {useInView} from '../../../hooks/useInView/useInView';
import {
  HorizontalList,
  HorizontalListSlider,
} from '../HorizontalList/HorizontalList';

interface Props {
  durationPerItemInMs?: number;
  testId?: string;
  className?: string;
  style?: React.CSSProperties;
  disableFade?: boolean;
}

const MS_DEFAULT_DURATION_PER_ITEM = 1000;

export const AutoScrollSlider: React.FC<React.PropsWithChildren<Props>> = ({
  durationPerItemInMs,
  className,
  style,
  testId,
  children,
  disableFade,
}) => {
  const {inView, ref: sliderRef} = useInView<HTMLDivElement>();
  const [currentScrollToSlide, setScrollToIndex] = useState(1);

  // eslint-disable-next-line local-rules/too-complex-useeffects, local-rules/prefer-use-inview-effect -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    if (!inView) return;
    const slideInterval = setInterval(() => {
      const childNodesCount = sliderRef.current?.childNodes.length ?? 0;

      sliderRef?.current?.scrollTo({
        left:
          ((sliderRef.current?.scrollWidth ?? 0) / childNodesCount) *
          currentScrollToSlide,
        behavior: 'smooth',
      });
      const newScrollToIndex = currentScrollToSlide + 1;
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setScrollToIndex(
        newScrollToIndex < childNodesCount ? newScrollToIndex : 0,
      );
    }, durationPerItemInMs || MS_DEFAULT_DURATION_PER_ITEM);

    return () => {
      clearInterval(slideInterval);
    };
  }, [currentScrollToSlide, durationPerItemInMs, sliderRef, inView]);

  return (
    <SliderWrapper
      ref={sliderRef}
      style={style}
      className={className}
      data-testid={testId ?? 'autoScrollSlider'}
      disableFade={disableFade}
    >
      {children}
    </SliderWrapper>
  );
};

const SliderWrapper = styled(HorizontalList)`
  ${HorizontalListSlider} {
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    flex-shrink: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;

    ${touchScroll};
    ${hideScrollBar};
  }
`;
